// utils/ChallengeUtils.tsx
import { useTranslation } from "react-i18next";
import IconDonFilled from "@/assets/icons/don-filled.svg?react";
import IconDonLinear from "@/assets/icons/don-linear.svg?react";
import IconDonFilledLinear from "@/assets/icons/don-filled-linear.svg?react";
import IconInfoCircle from "@/assets/icons/info-circle.svg?react";
import { formatNumber, calculateProgressPercentage } from "@/shared/utils";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import CommonBadge from "@/components/common/badge/CommonBadge.tsx";
import Youtube from "@/assets/icons/socials/youtube.svg?react";
import IconChevron from "@/assets/icons/chevron-right-bold.svg?react";
import CountdownTimer from "@/components/Time/CountdownTimer";

// Utility Components
export const ChallengeCardWrapper = ({
  children,
  className = "",
  isCompleted = false,
}: {
  children: React.ReactNode;
  className?: string;
  isCompleted?: boolean;
}) => {
  return (
    <div
      className={`rounded-xl border-b border-white/08 backdrop-blur-[10px] p-3 pt-2.5 relative overflow-hidden ${
        isCompleted
          ? "bg-[linear-gradient(83deg,rgba(251,199,108,0.08)_-31.43%,_rgba(235,161,50,0.08)_100%)]"
          : "bg-white/08"
      } ${className}`}
    >
      <div className="relative z-10">{children}</div>
      <img
        className="w-[161px] h-auto absolute right-0 top-0 z-0"
        src="images/patterns/challenge-info.webp"
        alt="Don pattern"
      />
    </div>
  );
};

export const ChallengeHeader = ({
  text,
  textColor = "text-gray-1",
}: {
  text: string;
  textColor?: string;
}) => {
  return (
    <p className={`text-xs font-normal leading-130 mb-1 ${textColor}`}>
      {text}
    </p>
  );
};

export const ChallengeAmount = ({
  amount,
  isCompleted = false,
}: {
  amount: string;
  isCompleted?: boolean;
}) => {
  return (
    <p className="flex items-center gap-2 text-2xl text-white font-bold leading-120">
      {isCompleted ? (
        <IconDonFilledLinear width="22" height="22" className="text-gray-1" />
      ) : (
        <IconDonFilled width="22" height="22" className="text-gray-1" />
      )}
      {formatNumber(amount)}
    </p>
  );
};

export const ChallengeProgressBar = ({
  gainedAmount,
  targetAmount,
}: {
  gainedAmount: string;
  targetAmount: string;
}) => {
  const { t } = useTranslation();
  const progressBar = calculateProgressPercentage(targetAmount, gainedAmount);

  return (
    <div className="mt-3">
      <div className="h-2 rounded-lg transition-all duration-1000 bg-white/08">
        <div
          className="rounded-lg h-full bg-blue"
          style={{ width: `${progressBar}%` }}
        />
      </div>
      <div className="flex-center-between mt-3">
        <p className="text-xs text-gray-1 leading-130 font-normal">
          {t("collected_don")}
        </p>
        <p className="flex-y-center text-xs text-white font-medium gap-0.5">
          <IconDonLinear width="12" height="12" />
          {formatNumber(gainedAmount)}
        </p>
      </div>
    </div>
  );
};

export const ChallengeTimer = ({ deadline }: { deadline: string }) => {
  return <CountdownTimer targetDate={deadline} />;
};

export const ChallengeInfoButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <VibratingButton className="" onClick={onClick}>
      <IconInfoCircle
        width="24"
        height="24"
        className="text-gray-1"
        strokeWidth="1.6"
      />
    </VibratingButton>
  );
};

export const ChallengeLiveBadge = ({
  youtubeLink,
}: {
  youtubeLink: string;
}) => {
  return (
    <a href={youtubeLink} target="_blank">
      <CommonBadge
        className="absolute top-0 right-3 pl-1.5 pr-0.5 py-0.5 flex gap-1 items-center leading-120"
        text="LIVE"
        color="red"
        prefix={<Youtube className="shrink-0" width="12" height="12" />}
        suffix={
          <IconChevron className="shrink-0 -ml-0.5" width="12" height="12" />
        }
      />
    </a>
  );
};
